import { Descriptions } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { transferClass } from '../../../api/campusClasses.api';
import { getTuitions } from '../../../api/transferPay.api';
import useAxios from '../../../hooks/useAxios';
import { IGetTuitions, ITuition } from '../../../models/transferPay.model';
import { RootState } from '../../../store/rootReducer';
import Button from '../../Button';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import ConfirmModal from '../../modals/ConfirmModal';
import CompleteTransfer1Modal from './modals/CompleteTransfer1Modal';
import CompleteTransfer2Modal from './modals/CompleteTransfer2Modal';
import CompleteTransfer3Modal from './modals/CompleteTransfer3Modal';
import { getCommonCodeLabel } from '../../../lib/store';

interface IProps {
  setTab: (tab: string) => void;
  memberCode: string;
  classStudentCode: string;
  classCode: string;
  timetableCode: number | null | undefined;
  getStudents: (classCode: string) => void;
}

const ClassTransferCalculation: FC<IProps> = ({
  setTab,
  memberCode,
  classStudentCode,
  classCode,
  timetableCode,
  getStudents,
}) => {
  const { t } = useTranslation();
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { data: res, isLoading, error, execute } = useAxios<IGetTuitions>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetTuitions>();
  const {
    data: sendRes,
    isLoading: sendIsLoading,
    error: sendError,
    execute: sendExecute,
  } = useAxios();

  const [confirmTransferVisible, setConfirmTransferVisible] = useState(false);
  const [completeTransfer1Visible, setCompleteTransfer1Visible] =
    useState(false);
  const [completeTransfer2Visible, setCompleteTransfer2Visible] =
    useState(false);
  const [completeTransfer3Visible, setCompleteTransfer3Visible] =
    useState(false);
  let payAmount = 0;
  let refundAmount = 0;

  useEffect(() => {
    if (classCode && chosenClass && (timetableCode || timetableCode === null)) {
      execute(getTuitions(chosenClass.classCode, { timetableCode }));
      execute2(getTuitions(classCode, { timetableCode: null }));
    }
  }, [classCode, chosenClass, timetableCode]);

  useEffect(() => {
    if (sendRes && !sendIsLoading) {
      if (chosenClass) {
        getStudents(chosenClass.classCode);
      }

      payAmount === refundAmount
        ? setCompleteTransfer1Visible(true)
        : payAmount > refundAmount
        ? setCompleteTransfer2Visible(true)
        : setCompleteTransfer3Visible(true);
    }
  }, [sendRes, sendIsLoading]);

  const calculateFinalAmount = (index: number) => {
    if (!res || !res2) return;

    const data1 = res.data[index];
    const data2 = res2.data[index];
    let finalAmount =
      data1.dayAmount * data1.lastCnt +
      data2.dayAmount * data2.restCnt -
      data1.monthTuition;

    //전반 시 정산수업료 오차로 추가 20240203
    finalAmount = Math.round(finalAmount / 10) * 10; 

    if (finalAmount > 0) payAmount += finalAmount;
    else refundAmount += finalAmount * -1;

    return finalAmount.toLocaleString();
  };

  const onTransferClass = () => {
    if (!timetableCode && !timetableCode === null) return;
    sendExecute(transferClass(classCode, classStudentCode, { timetableCode }));
  };

  return (
    <>
      {isLoading || isLoading2 || sendIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={sendError} />

      <div className="comm-bx__between">        
        <h3 className="sub-title">{res?.data[0]?.className}</h3>
      </div>
      {res?.data?.map((item: ITuition, index: number) => (
        <>
          <div className={(index === 0) ? "comm-bx__space" : "comm-bx__space mt-5"}>        
            <h4 className='sub-title'>- {getCommonCodeLabel('SY0009', item.monthNameCode.trim(), 'ko')}</h4>
          </div>
          <div className="comm-table__area rowType stats">
            <Descriptions
              bordered
              column={2}
              size="small"
              labelStyle={{ textAlign: 'center' }}
              contentStyle={{ textAlign: 'center', width: '33%' }}
            >
              <Descriptions.Item label={t('class.class.column57')}>
                {item.className}
              </Descriptions.Item>
              <Descriptions.Item>
                {res2?.data[index]?.className}
              </Descriptions.Item>
              <Descriptions.Item label={`${t('class.class.column58')}ⓐ`}>
                {item.monthTuition.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item>
                {res2?.data[index]?.monthTuition.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={`${t('class.class.column59')}ⓑ`}>
                {item.dayAmount.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item>
                {res2?.data[index]?.dayAmount.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={`${t('class.class.column60')}ⓒ`}>
                {item.lastCnt}
              </Descriptions.Item>
              <Descriptions.Item>{res2?.data[index]?.restCnt}</Descriptions.Item>
              <Descriptions.Item label={`${t('class.class.column61')}ⓓ=ⓑxⓒ`}>
                {(item.dayAmount * item.lastCnt).toLocaleString()} ⓔ
              </Descriptions.Item>
              <Descriptions.Item>
                {(
                  (res2?.data[index]?.dayAmount || 0) *
                  (res2?.data[index]?.restCnt || 0)
                ).toLocaleString()}{' '}
                ⓕ
              </Descriptions.Item>
              <Descriptions.Item label={`${t('class.class.column62')}ⓔ+ⓕ-ⓐ`}>
                {calculateFinalAmount(index)}
              </Descriptions.Item>
            </Descriptions>
            </div>
        </>
      ))}
      
      <div className="comm-table__area rowType">
        <Descriptions
          bordered
          column={1}
          size="small"
          labelStyle={{ textAlign: 'center' }}
          contentStyle={{ textAlign: 'center', width: '50%' }}
        >
          <Descriptions.Item label={t('class.class.column63')}>
            {refundAmount.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label={t('class.class.column64')}>
            {payAmount.toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      </div>  
      
      <div className="comm-bx__between">        
        <div className="comm-btn__area">
          <Button
            className="btn-basic gray-line"
            onClick={() => setTab('transfer')}
            text={t('common.button.back')}
          />
        </div>
        <div className="comm-btn__area">
          <Button
            className="btn-basic pink-line"
            onClick={() => setConfirmTransferVisible(true)}
            text={t('class.class.column38')}
          />
        </div>
      </div>

      {confirmTransferVisible && (
        <ConfirmModal
          visible={confirmTransferVisible}
          setVisible={setConfirmTransferVisible}
          onConfirm={onTransferClass}
          titleText={t('class.class.title8')}
          messageText={t('class.class.text21')}
          confirmText={t('class.class.column38')}
          cancelText={t('common.button.cancel')}
        />
      )}
      {completeTransfer1Visible && (
        <CompleteTransfer1Modal
          visible={completeTransfer1Visible}
          setVisible={setCompleteTransfer1Visible}
          setTab={setTab}
        />
      )}
      {completeTransfer2Visible && (
        <CompleteTransfer2Modal
          visible={completeTransfer2Visible}
          setVisible={setCompleteTransfer2Visible}
          setTab={setTab}
          memberCode={memberCode}
        />
      )}
      {completeTransfer3Visible && (
        <CompleteTransfer3Modal
          visible={completeTransfer3Visible}
          setVisible={setCompleteTransfer3Visible}
          setTab={setTab}
          memberCode={memberCode}
        />
      )}
    </>
  );
};

export default ClassTransferCalculation;
